import React, { useEffect } from 'react';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  icon?: React.ReactNode;
  accentColor?: 'sky' | 'emerald' | 'amber' | 'rose' | 'purple';
}
const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-6xl'
};
const accentStyles = {
  sky: 'from-sky-500 to-cyan-500',
  emerald: 'from-emerald-500 to-teal-500',
  amber: 'from-amber-500 to-orange-500',
  rose: 'from-rose-500 to-pink-500',
  purple: 'from-purple-500 to-indigo-500'
};
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  icon,
  accentColor
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto print:static print:overflow-visible print:h-auto" data-id="element-3819">
      <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 print:min-h-0 print:p-0 print:block print:h-auto" data-id="element-3820">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity print:hidden" onClick={onClose} data-id="element-3821" />

        {/* Modal */}
        <div className={`
            relative w-full ${sizeStyles[size]}
            bg-white dark:bg-gray-800
            rounded-t-2xl sm:rounded-2xl shadow-2xl
            transform transition-all
            max-h-[100dvh] sm:max-h-[90vh] flex flex-col
            print:max-w-none print:shadow-none print:rounded-none print:transform-none print:h-auto print:max-h-none
          `} data-id="element-3822">
          {/* Accent bar */}
          {accentColor && <div className={`h-1 bg-gradient-to-r ${accentStyles[accentColor]} rounded-t-2xl flex-shrink-0`} data-id="element-3823"></div>}

          {/* Mobile drag handle */}
          <div className="flex justify-center pt-2 pb-0 sm:hidden flex-shrink-0" data-id="element-3824">
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" data-id="element-3825" />
          </div>

          {/* Header */}
          <div className={`flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700 print:hidden flex-shrink-0 ${!accentColor ? 'rounded-t-2xl' : ''}`} data-id="element-3826">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-id="element-3827">
              {icon && <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${accentColor === 'sky' ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400' : accentColor === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : accentColor === 'amber' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' : accentColor === 'rose' ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400' : accentColor === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`} data-id="element-3828">
                  {icon}
                </div>}
              <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-3829">
                {title}
              </h2>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors flex-shrink-0" data-id="element-3830">
              <XIcon className="w-5 h-5" data-id="element-3831" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0 print:p-0 print:max-h-none print:overflow-visible print:h-auto" data-id="element-3832">
            {children}
          </div>
        </div>
      </div>
    </div>;
}